// src/components/CoursesShowcaseHome.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { collection, getDocs, query, limit } from "firebase/firestore";
import { db } from "./firebase/firebaseConfig";
import { gradients } from "./theme/theme";

// ===== Styled Components =====
const Section = styled.section`
  background: ${gradients.heroBackground};
  padding: 4rem 2rem;
  color: #fff;
`;

const Title = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: bold;
`;

const SliderViewport = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 1.5rem;
  scroll-snap-type: x mandatory;
  padding-bottom: 1rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CourseCard = styled.div`
  flex: 0 0 280px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.15);
  scroll-snap-align: start;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 10px 25px rgba(255, 122, 24, 0.4);
  }
`;

const CourseImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const CourseContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CourseTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 12px;
  font-weight: 600;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 16px;
`;

const Price = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: #3cff0b;
`;

const OldPrice = styled.span`
  font-size: 1rem;
  text-decoration: line-through;
  color: #aaa;
`;

const Discount = styled.span`
  font-size: 0.9rem;
  background: #ff1744;
  color: #fff;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 600;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
`;

const BuyButton = styled(Link)`
  flex: 1;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  text-align: center;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s;
  &:hover {
    background: linear-gradient(135deg, #ff5722, #e91e63);
  }
`;

const DetailsButton = styled(Link)`
  flex: 1;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #1e1e2f;
  text-align: center;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s;
  &:hover {
    background: linear-gradient(135deg, #0097a7, #00acc1);
  }
`;

const ViewAllCard = styled(CourseCard)`
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.07);
  cursor: pointer;
  color: white;
  &:hover {
    background: linear-gradient(90deg, #ff7a18, #ff0066);
    color: white;
  }
`;

// ===== Component =====
const CoursesShowcaseHome = () => {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const q = query(collection(db, "courses"), limit(3)); // fetch 3 featured
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCourses(data);
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };
    fetchCourses();
  }, []);

  return (
    <Section>
      <Title>📚 Featured Courses</Title>
      <SliderViewport>
        {courses.map((course) => {
          const discountedPrice =
            course.discount > 0
              ? (course.price - (course.price * course.discount) / 100).toFixed(
                  2
                )
              : course.price;

          return (
            <CourseCard key={course.id}>
              <CourseImage
                src={course.imageUrl || "https://via.placeholder.com/400x200"}
                alt={course.title}
              />
              <CourseContent>
                <CourseTitle>{course.title}</CourseTitle>

                <PriceRow>
                  <Price>
                    {course.price === 0 ? "Free" : `₹${discountedPrice}`}
                  </Price>
                  {course.discount > 0 && (
                    <>
                      <OldPrice>₹{course.price}</OldPrice>
                      <Discount>{course.discount}% OFF</Discount>
                    </>
                  )}
                </PriceRow>

                <ButtonRow>
                  <BuyButton to={`/checkout/${course.id}`}>Buy</BuyButton>
                  <DetailsButton to={`/courses/${course.id}`}>
                    Details
                  </DetailsButton>
                </ButtonRow>
              </CourseContent>
            </CourseCard>
          );
        })}

        {/* Last card → View All */}
        <ViewAllCard as={Link} to="/courses">
          View All Courses →
        </ViewAllCard>
      </SliderViewport>
    </Section>
  );
};

export default CoursesShowcaseHome;
